// src/components/shared/auth/ForgotPasswordView.jsx
import React, { useEffect, useRef, useState } from "react";
import { Mail, CheckCircle, XCircle, ArrowLeft, KeyRound, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import { authService } from "../../../services/auth.service";

const RESEND_COOLDOWN = 60; // seconds

export default function ForgotPasswordView() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // "email" → user enters email, "otp" → user enters 6-digit OTP
  const [step, setStep] = useState("email");

  // Shared state
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // OTP state — 6 individual digit boxes
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  // Resend cooldown
  const [cooldown, setCooldown] = useState(0);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 6000);
  };

  const clearMessage = () => setMessage({ type: "", text: "" });

  const otpValue = digits.join("");

  // ─── Step 1: Send OTP ───────────────────────────────────────────────────────

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!email.trim()) {
      showMessage("error", t("validation.enterEmail"));
      return;
    }
    setLoading(true);
    clearMessage();
    try {
      await authService.forgotPassword({ email: email.trim() });
      setStep("otp");
      setCooldown(RESEND_COOLDOWN);
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  // ─── Resend OTP ─────────────────────────────────────────────────────────────

  const handleResend = async () => {
    if (cooldown > 0 || loading) return;
    setLoading(true);
    clearMessage();
    try {
      await authService.forgotPassword({ email: email.trim() });
      setCooldown(RESEND_COOLDOWN);
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
      showMessage("success", t("auth.otp.resendSuccess"));
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  // ─── OTP digit input handlers ───────────────────────────────────────────────

  const handleDigitChange = (index, value) => {
    // Accept only a single digit
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    for (let i = 0; i < 6; i++) next[i] = pasted[i] || "";
    setDigits(next);
    const focusIndex = Math.min(pasted.length, 5);
    inputRefs.current[focusIndex]?.focus();
  };

  // ─── Step 2: Verify OTP ─────────────────────────────────────────────────────

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otpValue.length < 6) {
      showMessage("error", t("auth.otp.incompleteCode"));
      return;
    }
    setLoading(true);
    clearMessage();
    try {
      await authService.verifyOtp({ email: email.trim(), otp: otpValue });
      // OTP is valid — navigate to reset-password, pass email + otp via state
      navigate("/reset-password", { state: { email: email.trim(), otp: otpValue } });
    } catch (e) {
      showMessage("error", e?.message || t("auth.otp.invalidCode"));
      setDigits(["", "", "", "", "", ""]);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      <video className="absolute inset-0 w-full h-full object-cover" autoPlay loop muted playsInline>
        <source src="/login-bg.mp4" type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl my-6 p-8 shadow-[0_0_50px_rgba(255,0,255,0.25)] border border-white/20">
            <div className="flex justify-end mb-4">
              <LanguageSwitcher />
            </div>

            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-white/30 backdrop-blur-md">
                {step === "email"
                  ? <Mail className="w-10 h-10 text-pink-300" />
                  : <KeyRound className="w-10 h-10 text-pink-300" />
                }
              </div>
              <h2 className="text-3xl font-bold mb-2">
                {step === "email" ? t("auth.forgot.title") : t("auth.otp.title")}
              </h2>
              <p className="text-gray-200 text-sm">
                {step === "email"
                  ? t("auth.forgot.subtitle")
                  : t("auth.otp.subtitle", { email })
                }
              </p>
            </div>

            {/* Message banner */}
            {message.text && (
              <div className={`mb-6 p-4 rounded-xl text-sm flex items-start gap-3 border ${
                message.type === "success"
                  ? "bg-green-500/10 border-green-400 text-green-200"
                  : "bg-red-500/10 border-red-400 text-red-200"
              }`}>
                {message.type === "success"
                  ? <CheckCircle className="w-5 h-5 shrink-0" />
                  : <XCircle className="w-5 h-5 shrink-0" />
                }
                <span>{message.text}</span>
              </div>
            )}

            {/* ── Step 1: Email form ── */}
            {step === "email" && (
              <form onSubmit={handleSendOtp} className="space-y-6">
                <div>
                  <label className="block text-sm mb-2">{t("auth.forgot.emailLabel")}</label>
                  <div className="relative">
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 w-5 h-5" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("auth.forgot.emailPlaceholder")}
                      className="w-full pr-10 pl-4 py-3 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500 outline-none"
                      disabled={loading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 disabled:opacity-50 transition"
                >
                  {loading
                    ? <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("auth.forgot.sending")}
                      </span>
                    : t("auth.forgot.submit")
                  }
                </button>
              </form>
            )}

            {/* ── Step 2: OTP form ── */}
            {step === "otp" && (
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                {/* 6 digit boxes */}
                <div>
                  <label className="block text-sm mb-3 text-center">{t("auth.otp.label")}</label>
                  <div className="flex justify-center gap-3" onPaste={handleDigitPaste}>
                    {digits.map((d, i) => (
                      <input
                        key={i}
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={d}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(i, e)}
                        disabled={loading}
                        className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-white/10 border-2 border-white/20 text-white focus:border-pink-400 focus:ring-2 focus:ring-pink-400/30 outline-none transition disabled:opacity-50"
                      />
                    ))}
                  </div>
                  <p className="text-center text-xs text-white/50 mt-2">{t("auth.otp.hint")}</p>
                </div>

                <button
                  type="submit"
                  disabled={loading || otpValue.length < 6}
                  className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 disabled:opacity-50 transition"
                >
                  {loading
                    ? <span className="flex items-center justify-center gap-2">
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {t("auth.otp.verifying")}
                      </span>
                    : t("auth.otp.verify")
                  }
                </button>

                {/* Resend */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={cooldown > 0 || loading}
                    className="flex items-center justify-center gap-2 mx-auto text-sm text-pink-300 hover:text-pink-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {cooldown > 0
                      ? t("auth.otp.resendIn", { seconds: cooldown })
                      : t("auth.otp.resend")
                    }
                  </button>
                </div>

                {/* Back to email step */}
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setStep("email"); clearMessage(); setDigits(["","","","","",""]); }}
                    className="text-xs text-white/50 hover:text-white/80 transition"
                  >
                    {t("auth.otp.changeEmail")}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate("/login")}
                className="flex items-center justify-center gap-2 mx-auto text-pink-300 hover:text-pink-100 transition font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("auth.backToLogin")}
              </button>
            </div>
          </div>

          {/* Help card */}
          <div className="mt-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl p-6 shadow-[0_0_40px_rgba(255,0,255,0.15)]">
            <h3 className="font-semibold text-pink-200 mb-3">{t("auth.forgot.helpTitle")}</h3>
            <ul className="space-y-2 text-sm text-gray-200">
              <li className="flex items-start gap-2">
                <span className="text-pink-300 mt-0.5">-</span>
                <span>{t("auth.forgot.helpSpamCheck")}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-300 mt-0.5">-</span>
                <span>{t("auth.otp.validFor", { minutes: 10 })}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-pink-300 mt-0.5">-</span>
                <span>{t("auth.forgot.helpContactSupport")}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
